import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { translations } from '../translations';
import { 
  FileText, 
  Users, 
  Phone, 
  Calendar, 
  FolderOpen, 
  Briefcase,
  CheckCircle,
  MessageSquare,
  Mail,
  ArrowRight,
  Shield,
  Clock,
  Award,
  TrendingUp,
  Heart,
  Zap,
  Languages
} from 'lucide-react';

const LandingPage = () => {
  const [language, setLanguage] = useState('nl');
  const t = translations[language];
  const services = [
    {
      icon: <FileText className="w-10 h-10" />,
      title: t.services1.title,
      description: t.services1.description,
      benefits: t.services1.benefits
    },
    {
      icon: <FolderOpen className="w-10 h-10" />,
      title: t.services2.title,
      description: t.services2.description,
      benefits: t.services2.benefits
    },
    {
      icon: <Phone className="w-10 h-10" />,
      title: t.services3.title,
      description: t.services3.description,
      benefits: t.services3.benefits
    },
    {
      icon: <Calendar className="w-10 h-10" />,
      title: t.services4.title,
      description: t.services4.description,
      benefits: t.services4.benefits
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: t.services5.title,
      description: t.services5.description,
      benefits: t.services5.benefits
    },
    {
      icon: <Briefcase className="w-10 h-10" />,
      title: t.services6.title,
      description: t.services6.description,
      benefits: t.services6.benefits
    }
  ];

  const stats = [
    { number: "24u", label: t.stats.reactTime, icon: <Clock className="w-6 h-6" /> },
    { number: "7/7", label: t.stats.daysPerWeek, icon: <Calendar className="w-6 h-6" /> },
    { number: "6", label: t.stats.services, icon: <Briefcase className="w-6 h-6" /> },
    { number: "100%", label: t.stats.custom, icon: <Heart className="w-6 h-6" /> }
  ];

  const processSteps = [
    {
      number: "1",
      title: t.process1.title,
      description: t.process1.description,
      icon: <MessageSquare className="w-6 h-6" />
    },
    {
      number: "2",
      title: t.process2.title,
      description: t.process2.description,
      icon: <FileText className="w-6 h-6" />
    },
    {
      number: "3",
      title: t.process3.title,
      description: t.process3.description,
      icon: <Shield className="w-6 h-6" />
    },
    {
      number: "4",
      title: t.process4.title,
      description: t.process4.description,
      icon: <CheckCircle className="w-6 h-6" />
    },
    {
      number: "5",
      title: t.process5.title,
      description: t.process5.description,
      icon: <TrendingUp className="w-6 h-6" />
    }
  ];

  const whyReasons = [
    { icon: <Zap className="w-6 h-6" />, title: t.why1.title, desc: t.why1.desc },
    { icon: <Heart className="w-6 h-6" />, title: t.why2.title, desc: t.why2.desc },
    { icon: <Clock className="w-6 h-6" />, title: t.why3.title, desc: t.why3.desc },
    { icon: <Shield className="w-6 h-6" />, title: t.why4.title, desc: t.why4.desc },
    { icon: <Award className="w-6 h-6" />, title: t.why5.title, desc: t.why5.desc }
  ];

  const faqs = [
    {
      question: t.faq1.question,
      answer: t.faq1.answer
    },
    {
      question: t.faq2.question,
      answer: t.faq2.answer
    },
    {
      question: t.faq3.question,
      answer: t.faq3.answer
    },
    {
      question: t.faq4.question,
      answer: t.faq4.answer
    }
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#3b82f6]/10 to-transparent rounded-full blur-3xl -z-10" />
      <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#f97316]/10 to-transparent rounded-full blur-3xl -z-10" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md shadow-sm z-50 border-b border-slate-100">
        <div className="container mx-auto px-4 py-5 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.png" 
              alt="WorkHelp NL Logo" 
              className="h-20 w-auto"
            />
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#diensten" className="text-slate-700 hover:text-[#1e3a8a] font-medium transition-colors">{t.services}</a>
            <a href="#werkwijze" className="text-slate-700 hover:text-[#1e3a8a] font-medium transition-colors">{t.howItWorks}</a>
            <a href="#tarieven" className="text-slate-700 hover:text-[#1e3a8a] font-medium transition-colors">{t.pricingTag}</a>
            <a href="#waarom" className="text-slate-700 hover:text-[#1e3a8a] font-medium transition-colors">{t.why}</a>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 border-l border-slate-200 pl-6">
              <Languages className="w-4 h-4 text-slate-600" />
              <button
                onClick={() => setLanguage('nl')}
                className={`px-3 py-1 rounded-lg font-medium transition-all ${
                  language === 'nl' 
                    ? 'bg-[#1e3a8a] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                NL
              </button>
              <button
                onClick={() => setLanguage('pl')}
                className={`px-3 py-1 rounded-lg font-medium transition-all ${
                  language === 'pl' 
                    ? 'bg-[#dc2626] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                PL
              </button>
            </div>
            
            <Button 
              onClick={scrollToContact}
              className="bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#c2410c] text-white shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {t.contact} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </nav>
          <Button 
            onClick={scrollToContact}
            className="md:hidden bg-[#1e3a8a] hover:bg-[#1e40af] text-white"
          >
            {t.contact}
          </Button>
        </div>
      </header>

      {/* Hero Section - Enhanced */}
      <section className="pt-40 pb-24 px-4 relative">
        {/* Decorative Elements */}
        <div className="absolute top-32 left-10 w-20 h-20 bg-[#3b82f6]/20 rounded-full blur-2xl" />
        <div className="absolute top-48 right-20 w-32 h-32 bg-[#f97316]/20 rounded-full blur-2xl" />
        
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 relative z-10">
              <div className="flex flex-wrap gap-3 items-center">
                <Badge className="bg-white/90 backdrop-blur-sm text-[#1e3a8a] border border-[#3b82f6]/30 px-4 py-2 text-sm font-medium shadow-sm">
                  <Shield className="w-4 h-4 mr-2 inline" />
                  {t.openedOn}
                </Badge>
                <Badge className="bg-white/90 backdrop-blur-sm text-[#dc2626] border border-[#dc2626]/30 px-4 py-2 text-sm font-medium shadow-sm">
                  🇵🇱 {t.forPolishCommunity}
                </Badge>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-[#1e3a8a] leading-tight">
                {t.heroTitle}
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 leading-relaxed">
                {t.heroSubtitle}
              </p>
              <div className="flex items-center gap-3 text-lg text-slate-600">
                <CheckCircle className="w-6 h-6 text-[#f97316]" />
                <span className="font-medium">{t.heroTagline}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={scrollToContact}
                  size="lg"
                  className="bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#c2410c] text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {t.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white text-lg px-8 py-6 transition-all duration-300"
                  onClick={() => document.getElementById('diensten')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t.ctaSecondary}
                </Button>
              </div>
            </div>
            
            <div className="relative">
              {/* Decorative card behind */}
              <div className="absolute -top-8 -right-8 w-full h-full bg-gradient-to-br from-[#3b82f6]/20 to-[#1e3a8a]/20 rounded-3xl blur-xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img 
                  src="/hero-image.png" 
                  alt="WorkHelp NL - Professionele hulp" 
                  className="w-full h-[550px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/40 via-transparent to-transparent" />
              </div>
              
              {/* Floating stats card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-6 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#f97316] to-[#ea580c] rounded-xl flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#1e3a8a]">{t.newBadge}</div>
                    <div className="text-sm text-slate-600">{t.since}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4 group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110">
                  <div className="text-white">{stat.icon}</div>
                </div>
                <div className="text-5xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-lg text-slate-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Enhanced */}
      <section id="diensten" className="py-24 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium mb-4">
              {t.servicesTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6">
              {t.servicesTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t.servicesSubtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-[#3b82f6] overflow-hidden hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${
                      index % 3 === 0 ? 'from-[#3b82f6] to-[#1e3a8a]' : 
                      index % 3 === 1 ? 'from-[#f97316] to-[#ea580c]' : 
                      'from-[#1e40af] to-[#3b82f6]'
                    } rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {service.icon}
                    </div>
                    <Badge className="bg-slate-100 text-slate-600 text-base px-3 py-1">
                      0{index + 1}
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4 group-hover:text-[#3b82f6] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                        <CheckCircle className="w-4 h-4 text-[#f97316] flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Image */}
      <section className="py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-[#3b82f6]/10 to-transparent rounded-full blur-3xl" />
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-[#3b82f6]/20 to-[#1e3a8a]/20 rounded-3xl" />
              <img 
                src="https://images.unsplash.com/photo-1574966390692-5140d4310743" 
                alt="Persoonlijke ondersteuning" 
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
            </div>
            <div className="space-y-8">
              <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium">
                {t.aboutTag}
              </Badge>
              <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] leading-tight">
                {t.aboutTitle}
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  {t.aboutText1}
                  <span className="font-semibold text-[#1e3a8a]"> {t.aboutText1Bold}</span>
                </p>
                <div className="bg-gradient-to-r from-[#eff6ff] to-white p-6 rounded-2xl border-l-4 border-[#3b82f6]">
                  <p className="text-[#1e3a8a] font-medium">
                    "{t.aboutQuote}"
                  </p>
                </div>
                <p>
                  {t.aboutText2}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#3b82f6] to-[#1e3a8a] rounded-xl flex items-center justify-center mb-3">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-[#1e3a8a] mb-1">{t.aboutCard1}</div>
                  <div className="text-sm text-slate-600">{t.aboutCard1Sub}</div>
                </div>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#f97316] to-[#ea580c] rounded-xl flex items-center justify-center mb-3">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-[#1e3a8a] mb-1">{t.aboutCard2}</div>
                  <div className="text-sm text-slate-600">{t.aboutCard2Sub}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Werkwijze Section - Visual Timeline */}
      <section id="werkwijze" className="py-24 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium mb-4">
              {t.processTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6">
              {t.processTitle}
            </h2>
            <p className="text-xl text-slate-600">{t.processSubtitle}</p>
          </div>
          
          <div className="max-w-5xl mx-auto relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-[50%] w-1 h-[calc(100%-12rem)] bg-gradient-to-b from-[#3b82f6] via-[#1e3a8a] to-[#f97316] opacity-20" />
            
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Content Card */}
                  <div className={`w-full lg:w-[45%] ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <Card className="hover:shadow-xl transition-all duration-300 border-2 border-slate-100 hover:border-[#3b82f6] group">
                      <CardContent className="p-8">
                        <div className={`flex items-start gap-4 ${index % 2 === 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
                          <div className="flex-shrink-0">
                            <div className={`w-14 h-14 bg-gradient-to-br ${
                              index % 3 === 0 ? 'from-[#3b82f6] to-[#1e3a8a]' : 
                              index % 3 === 1 ? 'from-[#f97316] to-[#ea580c]' : 
                              'from-[#1e40af] to-[#3b82f6]'
                            } rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                              {step.icon}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-3">{step.title}</h3>
                            <p className="text-slate-600 leading-relaxed">{step.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Center Circle */}
                  <div className="flex-shrink-0 relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-white to-slate-50 border-4 border-[#3b82f6] rounded-full flex items-center justify-center shadow-xl">
                      <span className="text-3xl font-bold text-[#1e3a8a]">{step.number}</span>
                    </div>
                  </div>
                  
                  {/* Empty space for alignment */}
                  <div className="hidden lg:block w-[45%]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Why Section */}
      <section id="waarom" className="py-24 px-4 bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#3b82f6] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]" />
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-20">
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/20 px-4 py-2 text-sm font-medium mb-4">
              {t.whyTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t.whyTitle}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whyReasons.map((reason, index) => (
              <Card 
                key={index}
                className="bg-white/10 backdrop-blur-md border-2 border-white/20 hover:bg-white/20 transition-all duration-300 group hover:scale-105"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#f97316] to-[#ea580c] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-white">{reason.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{reason.title}</h3>
                  <p className="text-slate-200">{reason.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <section id="tarieven" className="py-24 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium mb-4">
              {t.pricingTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6">
              {t.pricingTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.pricingSubtitle}
            </p>
          </div>

          {/* Subscription Plans */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-[#1e3a8a] text-center mb-12">{t.subscriptions}</h3>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Basic Plan */}
              <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-slate-200">
                <CardContent className="p-8">
                  <h4 className="text-2xl font-bold text-[#1e3a8a] mb-2">{t.basicPlan}</h4>
                  <p className="text-slate-600 mb-6">{t.basicDesc}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-[#1e3a8a]">€{t.basicPrice}</span>
                    <span className="text-slate-600 ml-2">/ {t.perMonth}</span>
                  </div>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.basicFeature1}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.basicFeature2}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.basicFeature3}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    variant="outline"
                    className="w-full border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                  >
                    {t.choosePlan}
                  </Button>
                </CardContent>
              </Card>

              {/* Standard Plan - Popular */}
              <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-[#3b82f6] relative transform hover:-translate-y-2">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white px-4 py-1">
                    {t.popular}
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <h4 className="text-2xl font-bold text-[#1e3a8a] mb-2">{t.standardPlan}</h4>
                  <p className="text-slate-600 mb-6">{t.standardDesc}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-[#3b82f6]">€{t.standardPrice}</span>
                    <span className="text-slate-600 ml-2">/ {t.perMonth}</span>
                  </div>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.standardFeature1}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.standardFeature2}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.standardFeature3}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    className="w-full bg-gradient-to-r from-[#3b82f6] to-[#1e3a8a] text-white hover:from-[#1e3a8a] hover:to-[#3b82f6]"
                  >
                    {t.choosePlan}
                  </Button>
                </CardContent>
              </Card>

              {/* Premium Plan */}
              <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-slate-200">
                <CardContent className="p-8">
                  <h4 className="text-2xl font-bold text-[#1e3a8a] mb-2">{t.premiumPlan}</h4>
                  <p className="text-slate-600 mb-6">{t.premiumDesc}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-[#1e3a8a]">€{t.premiumPrice}</span>
                    <span className="text-slate-600 ml-2">/ {t.perMonth}</span>
                  </div>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.premiumFeature1}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.premiumFeature2}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t.premiumFeature3}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    variant="outline"
                    className="w-full border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                  >
                    {t.choosePlan}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* One-Time Services */}
          <div>
            <h3 className="text-3xl font-bold text-[#1e3a8a] text-center mb-12">{t.oneTimeServices}</h3>
            <div className="max-w-4xl mx-auto">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex justify-between items-center py-4 border-b border-slate-200">
                      <span className="text-slate-700 font-medium">{t.service1Name}</span>
                      <span className="text-[#1e3a8a] font-bold text-lg">{t.service1Price}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-b border-slate-200">
                      <span className="text-slate-700 font-medium">{t.service2Name}</span>
                      <span className="text-[#1e3a8a] font-bold text-lg">{t.service2Price}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-b border-slate-200">
                      <span className="text-slate-700 font-medium">{t.service3Name}</span>
                      <span className="text-[#1e3a8a] font-bold text-lg">{t.service3Price}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-b border-slate-200">
                      <span className="text-slate-700 font-medium">{t.service4Name}</span>
                      <span className="text-[#1e3a8a] font-bold text-lg">{t.service4Price}</span>
                    </div>
                    <div className="flex justify-between items-center py-4">
                      <span className="text-slate-700 font-medium">{t.service5Name}</span>
                      <span className="text-[#1e3a8a] font-bold text-lg">{t.service5Price}</span>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="bg-gradient-to-r from-[#eff6ff] to-white p-6 rounded-xl border-l-4 border-[#3b82f6]">
                      <p className="font-semibold text-[#1e3a8a] mb-2">{t.extraInfo}</p>
                      <p className="text-slate-600">{t.extraInfoSub}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <p className="text-center text-slate-500 mt-6 italic">{t.pricesExclVAT}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-20">
            <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium mb-4">
              {t.faqTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6">
              {t.faqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-2 border-slate-100 rounded-2xl px-8 bg-white hover:border-[#3b82f6] hover:shadow-lg transition-all duration-300"
              >
                <AccordionTrigger className="text-xl font-semibold text-[#1e3a8a] hover:text-[#3b82f6] py-8">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-lg leading-relaxed pb-8">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section - Enhanced */}
      <section id="contact" className="py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-[#3b82f6]/10 to-transparent rounded-full blur-3xl" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-[#eff6ff] text-[#1e3a8a] border-[#3b82f6]/20 px-4 py-2 text-sm font-medium mb-4">
              {t.contactTag}
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold text-[#1e3a8a] mb-6">
              {t.contactTitle}
            </h2>
            <p className="text-xl text-slate-600">{t.contactSubtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-[#25d366] group hover:-translate-y-2">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#25d366] to-[#128c7e] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <MessageSquare className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">{t.whatsapp}</h3>
                <p className="text-slate-600 mb-4">{t.whatsappDesc}</p>
                <p className="text-lg font-semibold text-slate-700">033 782 0565</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-[#3b82f6] group hover:-translate-y-2">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#3b82f6] to-[#1e3a8a] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">{t.phone}</h3>
                <p className="text-slate-600 mb-4">{t.phoneDesc}</p>
                <p className="text-lg font-semibold text-slate-700">033 782 0565</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-[#f97316] group hover:-translate-y-2">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#f97316] to-[#ea580c] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Mail className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">{t.email}</h3>
                <p className="text-slate-600 mb-4">{t.emailDesc}</p>
                <p className="text-lg font-semibold text-slate-700 break-all">info@workhelp.nl</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="bg-gradient-to-br from-[#eff6ff] to-white border-2 border-[#3b82f6]/20 rounded-3xl p-12 text-center">
            <Clock className="w-12 h-12 text-[#1e3a8a] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#1e3a8a] mb-3">{t.responseTime}</h3>
            <p className="text-lg text-slate-600">{t.responseTimeDesc}</p>
          </div>
        </div>
      </section>

      {/* Footer - Enhanced */}
      <footer className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="/logo.png" 
                alt="WorkHelp NL Logo" 
                className="h-20 w-auto mb-6 brightness-[3] saturate-0"
              />
              <p className="text-slate-300 leading-relaxed">
                {t.footerAbout}
              </p>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-6">{t.quickLinks}</h4>
              <div className="space-y-3">
                <a href="#diensten" className="block text-slate-300 hover:text-white transition-colors">{t.services}</a>
                <a href="#werkwijze" className="block text-slate-300 hover:text-white transition-colors">{t.howItWorks}</a>
                <a href="#waarom" className="block text-slate-300 hover:text-white transition-colors">{t.whyWorkHelp}</a>
                <a href="#contact" className="block text-slate-300 hover:text-white transition-colors">{t.contact}</a>
              </div>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-6">{t.legalLinks}</h4>
              <div className="space-y-3">
                <a 
                  href="https://customer-assets.emergentagent.com/job_dutch-bridge/artifacts/ym4maosl_Algemenevoorwarden.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-slate-300 hover:text-white transition-colors"
                >
                  {t.termsConditions}
                </a>
                <a 
                  href="https://customer-assets.emergentagent.com/job_dutch-bridge/artifacts/ny1a539e_AVG.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-slate-300 hover:text-white transition-colors"
                >
                  {t.privacy}
                </a>
                <a 
                  href="https://customer-assets.emergentagent.com/job_dutch-bridge/artifacts/8oa27j6e_Tariefoverzicht.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-slate-300 hover:text-white transition-colors"
                >
                  {t.pricing}
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-6">{t.contact}</h4>
              <div className="space-y-3">
                <p className="text-slate-300">info@workhelp.nl</p>
                <p className="text-slate-300">033 782 0565</p>
                <div className="flex items-center gap-2 mt-6">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm text-slate-300">{t.personalApproach}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-slate-300">{t.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
